import { useEffect, useState } from "react";
import type { GradientConfig } from "../../../../shared/api-contract";
import { useEditorStore } from "../../../../state/editor/useEditor";
import { useViewer } from "../../../../state/ViewerProvider";
import { CompactCheck, CompactColor, CompactSelect, CompactSlider, MiniBtn, Row } from "../../inspector/rows";

/** Live gradient-SVG background generator (v18, iJewel GradientSvgPlugin). */
export function GradientControl() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const [g, setG] = useState<GradientConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { r.getGradientConfig().then(setG).catch((e: Error) => setError(e.message)); }, [r]);
  if (!g) return error ? <p className="px-3 py-1.5 text-[11px] text-ed-red">{error}</p> : null;
  const gen = (patch: Partial<GradientConfig>) => {
    setBusy(true);
    return r.generateGradient(patch).then(
      (next) => {
        setError(null);
        setG(next);
        // Applying the gradient swaps the background for a generated SVG, so
        // Engine.getBackgroundColor() starts returning null (it returns null for
        // a texture). The Environment section's store-bound "Background" colour
        // row reads `scene.background` and would keep offering the colour the
        // gradient just replaced — and re-send it on the next click.
        if (next.applyToBackground) void store.refresh("scene");
      },
      // Silent before: a rejected generateGradient left the panel showing a
      // gradient the viewer never rendered.
      (e: Error) => setError(e.message),
    ).finally(() => setBusy(false));
  };
  return (
    <div className="flex flex-col">
      <Row label="Type"><CompactSelect value={g.type} options={[{ id: "linear", label: "Linear" }, { id: "radial", label: "Radial" }]} onChange={(v) => void gen({ type: v as "linear" | "radial" })} /></Row>
      {g.type === "linear" ? (
        <Row label="Angle"><CompactSlider value={g.angle} min={0} max={360} step={1} onChange={(v) => void gen({ angle: v })} /></Row>
      ) : (
        <Row label="Radius"><CompactSlider value={g.radius} min={0} max={1} step={0.01} onChange={(v) => void gen({ radius: v })} /></Row>
      )}
      <Row label="Color 1"><CompactColor value={g.color1} onChange={(v) => void gen({ color1: v })} /></Row>
      <Row label="Color 2"><CompactColor value={g.color2} onChange={(v) => void gen({ color2: v })} /></Row>
      <Row label="Apply to Background"><CompactCheck checked={g.applyToBackground} onChange={(v) => void gen({ applyToBackground: v })} /></Row>
      {/* MiniBtn (not a bare <button>) so the re-generate round-trip — SVG build
          + background swap in the viewer — shows a spinner and can't be
          double-fired while it is in flight. */}
      <Row label=""><MiniBtn icon="refresh" busy={busy} onClick={() => void gen({})}>Refresh</MiniBtn></Row>
      {error ? <span className="px-3 pb-1.5 text-[11px] text-ed-red">{error}</span> : null}
    </div>
  );
}
